// Permissions.tsx (FULL FILE)
import React, { useEffect, useMemo, useState, startTransition } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { SquarePen, ArrowLeft, Check, X, Save, PenLine } from "lucide-react";
import { useFormik } from "formik";
import axios from "axios";
import { showError } from "toast/toast";
import { PERMISSION_CONFIGS, PermissionConfig, FORCED_PERMISSIONS } from "./permissionConfigs";

import { getAuthCookie } from "../../utils/authCookies";

type Action = "create" | "view" | "edit" | "delete";
type PermKey = Action | "all";
const ACTIONS: Action[] = ["create", "view", "edit", "delete"];

type PermState = Record<
  string,
  { create: boolean; view: boolean; edit: boolean; delete: boolean; all: boolean }
>;

type BackendPerm = {
  _id: string;
  resource: string;
  action: Action;
  scope?: string;
};

interface PermissionsProps {
  config?: PermissionConfig;
  module?: "signage" | "brokerage";
}

export default function Permissions({ config, module = "signage" }: PermissionsProps) {
  const routePrefix = module === "brokerage" ? "realtor" : module;
  const matchEdit = useMatch(`/${routePrefix}/roles/:roleId/permissions/edit`);
  const mode: "view" | "edit" = matchEdit ? "edit" : "view";
  const isView = mode === "view";
  const token = getAuthCookie("sessionToken") || "";

  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  // Config
  const permissionConfig = config || PERMISSION_CONFIGS[module];
  const PERM_GROUPS = permissionConfig.groups;
  const RESOURCE_MAP = permissionConfig.resourceMap;

  // UI state
  const [roleName, setRoleName] = useState("Role");
  const [saving, setSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);

  // Data state
  const [permissionLookup, setPermissionLookup] = useState<Record<string, string>>({}); // "resource:action" -> _id
  const [idToKey, setIdToKey] = useState<Record<string, { resource: string; action: Action }>>({});
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [permState, setPermState] = useState<PermState>({});

  // gating
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ---------- Helpers ----------
  const buildEmptyPermState = (): PermState => {
    const next: PermState = {};
    for (const group of PERM_GROUPS) {
      for (const row of group.rows) {
        next[row] = { create: false, view: false, edit: false, delete: false, all: false };
      }
    }
    return next;
  };

  const applySelectedToState = (
    base: PermState,
    ids: string[],
    idToMeta: Record<string, { resource: string; action: Action }>
  ): PermState => {
    const next = { ...base };
    for (const id of ids) {
      const meta = idToMeta[id];
      if (!meta) continue;
      const rowLabel = Object.keys(RESOURCE_MAP).find(
        (label) => (RESOURCE_MAP as Record<string, string>)[label] === meta.resource
      );
      if (!rowLabel || !next[rowLabel]) continue;
      next[rowLabel][meta.action] = true;
    }
    // compute "all"
    for (const row of Object.keys(next)) {
      const s = next[row];
      s.all = !!(s.create && s.view && s.edit && s.delete);
    }
    return next;
  };

  const getRowActionIds = (rowLabel: string): Record<Action, string | undefined> => {
    const resource = (RESOURCE_MAP as Record<string, string>)[rowLabel];
    const out: Record<Action, string | undefined> = { create: undefined, view: undefined, edit: undefined, delete: undefined };
    if (!resource) return out;
    for (const a of ACTIONS) out[a] = permissionLookup[`${resource}:${a}`];
    return out;
  };

  const addIds = (ids: (string | undefined)[]) =>
    setSelectedPermissionIds((prev) => Array.from(new Set([...prev, ...ids.filter(Boolean) as string[]])));

  const removeIds = (ids: (string | undefined)[]) => {
    const removeSet = new Set(ids.filter(Boolean) as string[]);
    setSelectedPermissionIds((prev) => prev.filter((id) => !removeSet.has(id)));
  };

  // ---------- Fetch role + datatable in parallel ----------
  useEffect(() => {
    if (!roleId) return;

    const controller = new AbortController();
    let active = true;

    const load = async () => {
      setLoading(true);
      setReady(false);
      setLoadError(null);

      try {
        const [permRes, roleRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/permissions/datatable`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: 1,
              limit: 500,
              search: "",
              // Dynamically set scope based on module prop
              filters: { scope: module },
              sort: { field: "resource", order: "asc" },
              fields: ["_id", "resource", "action", "scope"],
            }),
            signal: controller.signal,
          }),
          fetch(`${process.env.REACT_APP_API_URL}/roles/${roleId}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }),
        ]);

        if (!active) return;

        if (!permRes.ok) throw new Error("Failed to fetch permissions datatable");
        if (!roleRes.ok) throw new Error("Failed to fetch role");

        const permJson = await permRes.json();
        const roleJson = await roleRes.json();

        const list: BackendPerm[] = Array.isArray(permJson?.data) ? permJson.data : [];
        // Build lookups
        const byKey: Record<string, string> = {};
        const reverse: Record<string, { resource: string; action: Action }> = {};
        for (const p of list) {
          if (!p?._id || !p?.resource || !p?.action) continue;
          byKey[`${p.resource}:${p.action}`] = p._id;
          reverse[p._id] = { resource: p.resource, action: p.action };
        }

        const incomingSelected: string[] = Array.isArray(roleJson?.permission_ids)
          ? roleJson.permission_ids.filter(Boolean)
          : [];

        // If module has forced permissions (eg. signage -> active_installations create/edit/delete),
        // ensure those IDs are present and checked initially.
        const forcedForModule = FORCED_PERMISSIONS[module] || {};
        const forcedIds: string[] = [];
        for (const [res, actions] of Object.entries(forcedForModule)) {
          for (const a of actions) {
            const id = byKey[`${res}:${a}`];
            if (id) forcedIds.push(id);
          }
        }

        const augmentedSelected = Array.from(new Set([...incomingSelected, ...forcedIds]));

        // Build initial UI state in one go (avoid flicker)
        const base = buildEmptyPermState();
        const initialPermState = applySelectedToState(base, augmentedSelected, reverse);

        startTransition(() => {
          setPermissionLookup(byKey);
          setIdToKey(reverse);
          setSelectedPermissionIds(augmentedSelected);
          if (roleJson?.name) setRoleName(roleJson.name);
          setPermState(initialPermState);
          setReady(true);
          setLoading(false);
        });
      } catch (e: any) {
        if (controller.signal.aborted || !active) return;
        console.error(e);
        setLoadError(e?.message || "Failed to load role & permissions");
        setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [roleId, token, module]); // module included to refetch when scope changes

  // ---------- Inline rename ----------
  const renameFormik = useFormik({
    enableReinitialize: true,
    initialValues: { roleName },
    onSubmit: () => {},
  });

  const handleRenameSave = async () => {
    const newName = renameFormik.values.roleName.trim();
    if (!newName || !roleId || newName === roleName) return;

    setRenaming(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/roles/${roleId}`,
        { name: newName },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (response.data) {
        setRoleName(newName);
        renameFormik.resetForm({ values: { roleName: newName } });
        setRenameOpen(false);
      }
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.response?.data?.message || "Failed to rename role");
    } finally {
      setRenaming(false);
    }
  };

  const handleRenameCancel = () => {
    renameFormik.resetForm({ values: { roleName } });
    setRenameOpen(false);
  };

  const renameDisabled =
    renaming || !renameFormik.values.roleName.trim() || renameFormik.values.roleName.trim() === roleName;

  // ---------- Toggle logic ----------
  const toggleCell = (row: string, key: PermKey) => {
    if (isView) return;

    setPermState((prev) => {
      const next = { ...prev };
      const existing = next[row] || { create: false, view: false, edit: false, delete: false, all: false };
      const current = { ...existing };

      const actionIds = getRowActionIds(row);
      
      // Check if this row is Brokerage Firms
      const resource = (RESOURCE_MAP as Record<string, string>)[row];
      const isBrokerageFirms = resource === "brokerage_firms";
      
      // If this resource/actions are forced for this module, prevent toggling those actions.
      const forcedForModule = FORCED_PERMISSIONS[module] || {};
      const forcedActionsForResource: string[] = forcedForModule[resource] || [];
      if (key !== "all" && forcedActionsForResource.includes(key as string)) {
        return prev; // ignore toggle for forced actions
      }

      if (key === "all") {
        const nextVal = !current.all;
        current.all = nextVal;
        if (nextVal) {
          current.create = current.view = current.edit = current.delete = true;
          const idsToAdd = ACTIONS.map((a) => actionIds[a]);
          
          // If Brokerage Firms, also add brokerage_agents and brokerage_team_members IDs
          if (isBrokerageFirms) {
            ACTIONS.forEach((action) => {
              const agentId = permissionLookup[`brokerage_agents:${action}`];
              const teamMemberId = permissionLookup[`brokerage_team_members:${action}`];
              if (agentId) idsToAdd.push(agentId);
              if (teamMemberId) idsToAdd.push(teamMemberId);
            });
          }
          
          addIds(idsToAdd);
        } else {
          current.create = current.view = current.edit = current.delete = false;
          const idsToRemove = ACTIONS.map((a) => actionIds[a]);
          
          // If Brokerage Firms, also remove brokerage_agents and brokerage_team_members IDs
          if (isBrokerageFirms) {
            ACTIONS.forEach((action) => {
              const agentId = permissionLookup[`brokerage_agents:${action}`];
              const teamMemberId = permissionLookup[`brokerage_team_members:${action}`];
              if (agentId) idsToRemove.push(agentId);
              if (teamMemberId) idsToRemove.push(teamMemberId);
            });
          }
          
          removeIds(idsToRemove);
        }
      } else {
        const action = key as Action;
        const nextVal = !current[action];
        current[action] = nextVal;
        const id = actionIds[action];
        
        if (id) {
          if (nextVal) {
            const idsToAdd = [id];
            
            // If Brokerage Firms, also add corresponding brokerage_agents and brokerage_team_members IDs
            if (isBrokerageFirms) {
              const agentId = permissionLookup[`brokerage_agents:${action}`];
              const teamMemberId = permissionLookup[`brokerage_team_members:${action}`];
              if (agentId) idsToAdd.push(agentId);
              if (teamMemberId) idsToAdd.push(teamMemberId);
            }
            
            addIds(idsToAdd);
            
            // If edit is being checked, automatically check view as well
            if (action === "edit" && !current.view) {
              current.view = true;
              const viewId = actionIds["view"];
              const viewIdsToAdd = viewId ? [viewId] : [];
              
              // If Brokerage Firms, also add view for agents and team members
              if (isBrokerageFirms) {
                const agentViewId = permissionLookup[`brokerage_agents:view`];
                const teamMemberViewId = permissionLookup[`brokerage_team_members:view`];
                if (agentViewId) viewIdsToAdd.push(agentViewId);
                if (teamMemberViewId) viewIdsToAdd.push(teamMemberViewId);
              }
              
              if (viewIdsToAdd.length > 0) {
                addIds(viewIdsToAdd);
              }
            }
            
            // Special behavior for signage -> active_installations:
            // When 'view' is checked by user, programmatically set 'all' to true.
            // When 'view' is unchecked, 'all' becomes false (handled below).
            if (resource === "active_installations" && action === "view") {
              // ensure other actions appear checked in UI (they are forced) and include their IDs
              current.all = true;
              const allIdsToAdd = ACTIONS.map((a) => actionIds[a]);
              addIds(allIdsToAdd);
              // Also include signage_team_members:view when in signage module
              if (module === "signage") {
                const signageTeamViewId = permissionLookup["signage_team_members:view"];
                if (signageTeamViewId) addIds([signageTeamViewId]);
              }
            }
          } else {
            const idsToRemove = [id];
            
            // If Brokerage Firms, also remove corresponding brokerage_agents and brokerage_team_members IDs
            if (isBrokerageFirms) {
              const agentId = permissionLookup[`brokerage_agents:${action}`];
              const teamMemberId = permissionLookup[`brokerage_team_members:${action}`];
              if (agentId) idsToRemove.push(agentId);
              if (teamMemberId) idsToRemove.push(teamMemberId);
            }
            
            removeIds(idsToRemove);
            // If user removed 'view' for active_installations, also remove signage_team_members:view
            if (resource === "active_installations" && action === "view" && module === "signage") {
              const signageTeamViewId = permissionLookup["signage_team_members:view"];
              if (signageTeamViewId) removeIds([signageTeamViewId]);
            }
          }
        }
        
        current.all = !!(current.create && current.view && current.edit && current.delete);
      }

      next[row] = current;
      return next;
    });
  };

  // Determine if a particular cell should be disabled (either view-mode or forced)
  const shouldDisableCell = (row: string, key: PermKey) => {
    if (isView) return true;
    const resource = (RESOURCE_MAP as Record<string, string>)[row];
    const forcedForModule = FORCED_PERMISSIONS[module] || {};
    const forcedActionsForResource: string[] = forcedForModule[resource] || [];
    // Disable the "all" checkbox for active_installations (user must not click it)
    if (key === "all" && resource === "active_installations") return true;
    if (key !== "all" && forcedActionsForResource.includes(key as string)) return true;
    return false;
  };
  // Check if a cell is specifically forced (so we can show a tooltip / visual disabled state)
  const isForcedCell = (row: string, key: PermKey) => {
    const resource = (RESOURCE_MAP as Record<string, string>)[row];
    const forcedForModule = FORCED_PERMISSIONS[module] || {};
    const forcedActionsForResource: string[] = forcedForModule[resource] || [];
    return key !== "all" && forcedActionsForResource.includes(key as string);
  };

  // ---------- Actions ----------
  const handleBack = () => navigate(-1);
  const goEdit = () => navigate(`/${routePrefix}/roles/${roleId}/permissions/edit`);
  const handleCancel = () => navigate(-1);

  const handleSave = async () => {
    if (!roleId) return;
    try {
      setSaving(true);
      
      // Get users:view permission ID from lookup
      // const usersViewPermissionId = permissionLookup["users:view"];
      
      // // Prepare permission IDs array
      // let permissionIdsToSend = [...selectedPermissionIds];
      
      // // If at least one permission is selected and users:view permission exists,
      // // automatically include users:view permission ID
      // if (selectedPermissionIds.length > 0 && usersViewPermissionId) {
      //   // Only add if not already present
      //   if (!permissionIdsToSend.includes(usersViewPermissionId)) {
      //     permissionIdsToSend.push(usersViewPermissionId);
      //   }
      // }

      const permissionIdsToSend = [...selectedPermissionIds];
      
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/roles/${roleId}`,
        { permission_ids: permissionIdsToSend },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      navigate(-1);
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.response?.data?.message || "Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Derived ----------
  const hasData = useMemo(() => Object.keys(permState).length > 0, [permState]);

  return (
    <div className="container">
      <div className="mt-4">
        <h2>Roles & Permissions</h2>
        <p className="text-muted fs-13">Manage user access and permissions for different modules and features</p>
      </div>

      <div className="shadow-border-1 box-shadow-datatables p-4 bg-white mt-3">
        <div className="d-flex justify-content-between align-items-start align-items-md-center mb-4 w-100">
          <div className="w-100">
            {/* TOP ROW */}
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
              {!renameOpen && (
                <div className="d-flex align-items-center gap-2">
                  <div>
                    <h5 className="mb-1">{isView ? "View Permissions" : "Edit Permissions"} - {roleName}</h5>
                    <div className="fs-14">
                      {isView ? "Review role permissions and assignments" : "Modify role permissions and assignments"}
                    </div>
                  </div>

                  <span
                    className="badge rounded-3 bg-warning-subtle text-warning-emphasis border border-warning-subtle d-inline-flex align-items-center justify-content-center ms-2"
                    style={{ width: 40, height: 40, cursor: "pointer" }}
                    title="Rename Role"
                    onClick={() => setRenameOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setRenameOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <SquarePen size={16} />
                  </span>
                </div>
              )}

              {renameOpen && (
                <div className="row g-2 g-md-3 align-items-end col-lg-5">
                  <div className="align-items-baseline col-12 d-flex gap-3">
                    <input
                      type="text"
                      name="roleName"
                      className="form-control br-8 rs__control"
                      placeholder="Enter Role Name"
                      value={renameFormik.values.roleName}
                      onChange={renameFormik.handleChange}
                      style={{ height: 35 }}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn btn-success br-8 mt-2 add"
                      disabled={renameDisabled}
                      title="Save Role Name"
                      onClick={handleRenameSave}
                    >
                      {renaming ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <Check size={20} />
                      )}
                    </button>

                    <button type="button" className="btn mt-2 br-8 close" onClick={handleRenameCancel}>
                      <X size={20} />
                    </button>
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center gap-2">
                <button type="button" className="btn close br-8" onClick={handleBack}>
                  <ArrowLeft size={16} className="me-2" />
                  Back to Roles
                </button>

                {isView ? (
                  <button type="button" className="btn btn-primary table-addbutton" onClick={goEdit}>
                    <PenLine size={16} className="me-2" />
                    Edit Permissions
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn close br-8" onClick={handleCancel}>
                      <X size={16} className="me-2" />
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="add br-8 btn fs-14 fw-bold"
                      disabled={saving || !ready}
                      onClick={handleSave}
                    >
                      {saving ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                      ) : (
                        <Check size={16} className="me-2" />
                      )}
                      Save Changes
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section header */}
        <div className="d-flex align-items-center mb-5">
          <div className="fw-semibold">Permissions & Assignment</div>
        </div>

        {/* Loading / Error */}
        {!ready && (
          <div className="p-3 d-flex align-items-center gap-2">
            {loading && (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                <span className="text-muted small">Loading role & permissions…</span>
              </>
            )}
            {loadError && <span className="text-danger small">{loadError}</span>}
          </div>
        )}

        {/* Accordion */}
        {ready && hasData && (
          <div className="accordion" id="permAccordion">
            {PERM_GROUPS.map((g, idx) => (
              <div className="accordion-item mb-3 border br-8" key={g.key}>
                <h2 className="accordion-header br-8">
                  <button
                    className={`accordion-button acc-head-center ${idx !== 0 ? "collapsed" : ""} br-8`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-${g.key}`}
                    aria-expanded={idx === 0 ? "true" : "false"}
                  >
                    <span className="fs-15 fw-bold mx-auto pe-4">{g.title}</span>
                  </button>
                </h2>

                <div
                  id={`collapse-${g.key}`}
                  className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                  data-bs-parent="#permAccordion"
                >
                  <div className="accordion-body p-0 perm-accordion-body">
                    {/* ≥ md: table */}
                    <div className="table-responsive perm-desktop">
                      <table className="table mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <td style={{ width: "36%" }} className="fs-14">
                              Module
                            </td>
                            <td className="text-center fs-14">Create</td>
                            <td className="text-center fs-14">View</td>
                            <td className="text-center fs-14">Edit</td>
                            <td className="text-center fs-14">Delete</td>
                            <td className="text-center fs-14">All</td>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r}>
                              <td className="fs-14 py-3 text-dark">{r}</td>
                              {(["create", "view", "edit", "delete", "all"] as const).map((k) => {
                                const forced = isForcedCell(r, k);
                                const cls = `${k === "all" ? "perm-checkbox-all" : "perm-checkbox-individual"}${forced ? " forced" : ""}`;
                                return (
                                <td key={k} className="text-center">
                                  <input
                                    type="checkbox"
                                      className={cls}
                                    checked={!!permState[r]?.[k]}
                                    onChange={() => toggleCell(r, k)}
                                      disabled={shouldDisableCell(r, k)}
                                      title={forced ? "Required for this resource — cannot modify" : undefined}
                                      aria-disabled={shouldDisableCell(r, k)}
                                  />
                                </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* < md: cards */}
                    <div className="perm-mobile px-2">
                      {g.rows.map((r) => (
                        <div key={r} className="border rounded-3 bg-white p-3 mb-2 shadow-sm">
                          <div className="fw-semibold mb-2">{r}</div>
                          <div className="perm-grid">
                            {(["create", "view", "edit", "delete", "all"] as const).map((k) => {
                              const forced = isForcedCell(r, k);
                              const cls = `${k === "all" ? "perm-checkbox-all" : "perm-checkbox-individual"}${forced ? " forced" : ""}`;
                              return (
                              <div key={k} className="perm-cell">
                                <span className="small text-muted text-capitalize">{k}</span>
                                <input
                                  type="checkbox"
                                    className={cls}
                                  checked={!!permState[r]?.[k]}
                                  onChange={() => toggleCell(r, k)}
                                    disabled={shouldDisableCell(r, k)}
                                    title={forced ? "Required for this resource — cannot modify" : undefined}
                                    aria-disabled={shouldDisableCell(r, k)}
                                />
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .acc-head-center { position: relative; justify-content: center; background-color: #fbf9fa; padding:12px }
        .acc-head-center:hover { background-color: #f6f3f4; }
        .acc-head-center::after { position: absolute; right: 1rem; }

        .perm-desktop { display: none; }
        .perm-mobile  { display: block; }
        @media (min-width: 768px) {
          .perm-desktop { display: block; }
          .perm-mobile  { display: none; }
        }

        .perm-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px 10px;
          align-items: center;
        }
        @media (max-width: 380px) {
          .perm-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .perm-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          flex-direction: column;
        }

        .perm-accordion-body { width: 100%; }
        .accordion-button::after { transform: rotate(270deg); }
        .accordion-button:not(.collapsed)::after { transform: rotate(0deg); }

        .add {
          background-color: #00a63e !important;
          color: white !important;
        }
        .add:disabled, .add:disabled:hover, .add:disabled:focus {
          opacity: 0.5 !important; color: white;
        }
        .close { background-color: #ffffff; border: 0.5px solid #d1d5dc; }
        .close:hover { background-color: #f3f4f6; border: 0.5px solid #d1d5dc; }

        /* Custom checkbox styling for individual permissions */
        .perm-checkbox-individual {
          width: 20px; height: 20px; cursor: pointer; appearance: none;
          border: 2px solid #dee2e6; border-radius: 4px; background-color: white; position: relative;
          transition: all 0.2s ease;
        }
        .perm-checkbox-individual:checked {
          background-color: #0d6efd !important; border-color: #0d6efd !important;
        }
        .perm-checkbox-individual:checked::after {
          content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 12px; height: 12px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20,6 9,17 4,12'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: center; background-size: contain;
        }
        .perm-checkbox-individual:focus { outline: none; box-shadow: 0 0 0 3px rgba(13,110,253,.25); }

        /* "All" checkbox - Green */
        .perm-checkbox-all {
          width: 20px; height: 20px; cursor: pointer; appearance: none;
          border: 2px solid #dee2e6; border-radius: 4px; background-color: white; position: relative;
          transition: all 0.2s ease;
        }
        .perm-checkbox-all:checked { background-color: #00a63e !important; border-color: #00a63e !important; }
        .perm-checkbox-all:checked::after {
          content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 12px; height: 12px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20,6 9,17 4,12'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: center; background-size: contain;
        }
        .perm-checkbox-all:focus { outline: none; box-shadow: 0 0 0 3px rgba(0,166,62,.25); }
        /* Forced (auto-checked) permissions: visually indicate disabled/required */
        .perm-checkbox-individual.forced,
        .perm-checkbox-all.forced {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}